import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  Modal,
  Typography,
  IconButton,
} from "@mui/material";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import dayjs from "dayjs";
import { ShieldCloseIcon } from "lucide-react";
import { UserListType } from "../ManageOrganization/Managecredits/manangeCreditType";

type ReportType = "summary" | "detail";

interface DownloadReportModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  userLists: UserListType[];
  onDownload: (
    reportType: ReportType,
    payload: {
      type: string;
      user: string;
      startDate: Date;
      endDate: Date;
      organizationId: string;
    }
  ) => void;
}

const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  open,
  onClose,
  organizationId,
  onDownload,
  userLists
}) => {
  const [filters, setFilters] = useState({
    timeFrame: "yearlyCL",
    type: "All",
    user: "All",
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(new Date().getFullYear(), 11, 31),
  });

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const today = new Date();

  


  const handleTimeFrameChange = (value: string) => {
    const now = new Date();
    let startDate: Date = now;
    let endDate: Date = now;

    switch (value) {
      case "yearlyCL":
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case "yearlyFY":
        startDate = new Date(now.getFullYear(), 3, 1);
        endDate = new Date(now.getFullYear() + 1, 2, 31);
        break;
      case "monthly":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case "weekly":
        const dayOfWeek = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
        startDate = monday;
        endDate = new Date(monday);
        endDate.setDate(monday.getDate() + 6);
        break;
      case "custom":
        setOpenDatePicker(true);
        return;
    }

    setFilters((prev) => ({
      ...prev,
      timeFrame: value,
      startDate,
      endDate,
    }));
  };


  const handleSaveCustomRange = () => {
    if (dateRange?.from && dateRange?.to) {
      const from = new Date(dateRange.from);
      const to = new Date(dateRange.to);
      if (from > today || to > today) return;

      setFilters((prev) => ({
        ...prev,
        timeFrame: "custom",
        startDate: from,
        endDate: to,
      }));
      setDateRange({ from: undefined, to: undefined });

      setOpenDatePicker(false);
    }
  };
  // const handleSaveCustomRange = () => {
  //   if (dateRange?.from && dateRange?.to) {
  //     const from = new Date(dateRange.from);
  //     const to = new Date(dateRange.to);
  //     if (from > today || to > today) return;

  //     setFilters((prev) => ({
  //       ...prev,
  //       timeFrame: "custom",
  //       startDate: from,
  //       endDate: to,
  //     }));

  //     setOpenDatePicker(false);
  //   }
  // };

  const handleDownload = (reportType: ReportType) => {
    const { startDate, endDate, type, user } = filters;
    if (!startDate || !endDate) {
      alert("Please select a valid date range");
      return;
    }

    onDownload(reportType, {
      startDate,
      endDate,
      organizationId,
      type,
      user,
    });


    // onClose();
  };

  const handleCloseModel = () => {
    setFilters({
      timeFrame: "yearlyCL",
      type: "All",
      user: "All",
      startDate: new Date(new Date().getFullYear(), 0, 1),
      endDate: new Date(new Date().getFullYear(), 11, 31),
    })
    onClose();
  }
  const handleOpenCustomPicker = () => {
    if (filters.timeFrame === "custom") {
      setDateRange({ from: filters.startDate, to: filters.endDate });
    }
    setOpenDatePicker(true);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Download Report

        <IconButton
          aria-label="close"
          onClick={handleCloseModel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <ShieldCloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Date Range</InputLabel>
                    <Select
                      value={filters.timeFrame}
                      onChange={(e) => handleTimeFrameChange(e.target.value)}
                      label="Select Date Range"
                    >
                      <MenuItem value="weekly">This Week</MenuItem>
                      <MenuItem value="monthly">This Month</MenuItem>
                      <MenuItem value="yearlyCL">This calendar year</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>User</InputLabel>
                    <Select
                      value={filters.user}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, user: e.target.value }))
                      }
                      label="User"
                    >
                      {userLists.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, type: e.target.value }))
                      }
                      label="Type"
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="IDP">IDP</MenuItem>
                      <MenuItem value="Ocean">Ocean</MenuItem>
                      <MenuItem value="Air">Air</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  {/* <Typography variant="body2" color="textSecondary">
                    Selected Date Range:{" "}
                    <strong>
                      {dayjs(filters.startDate).format("DD MMM YYYY")} -{" "}
                      {dayjs(filters.endDate).format("DD MMM YYYY")}
                    </strong>
                  </Typography> */}

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ cursor: "pointer" }}
                    onClick={() => filters.timeFrame === "custom" && handleOpenCustomPicker()}
                  >
                    Selected Date Range:{" "}
                    <strong>
                      {dayjs(filters.startDate).format("DD MMM YYYY")} -{" "}
                      {dayjs(filters.endDate).format("DD MMM YYYY")}
                    </strong>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Modal open={openDatePicker} onClose={() => setOpenDatePicker(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              width: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Select Custom Date Range
            </Typography>
            <DayPicker
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              disabled={{ after: today }}
            />
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button variant="outlined" onClick={() => setOpenDatePicker(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveCustomRange}>
                Save
              </Button>
            </Box>
          </Box>
        </Modal>
      </DialogContent>

      <DialogActions>
        <Button className="btnTheme " onClick={() => handleDownload("summary")} variant="contained">
          Download Summary
        </Button>
        <Button className="btnTheme" onClick={() => handleDownload("detail")} variant="outlined">
          Download Detail
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadReportModal;
